import * as Sentry from '@sentry/react';
import { type MutableRefObject, useCallback, useRef, useState } from 'react';
import {
  backgroundBlurAbortError,
  throwIfBlurAborted,
} from '@/lib/background-blur/background-blur-error';
import {
  SoftwareBackgroundBlur,
  SoftwareBackgroundBlurError,
} from '@/lib/background-blur/software-background-blur';
import { waitForOperation } from '@/lib/background-blur/wait-for-operation';
import { getCallId, wlog } from '@/lib/webrtc-logger';

export type BackgroundBlurStatus =
  'off' | 'starting' | 'active' | 'unavailable';
export type BackgroundBlurMode = 'native' | 'software' | null;
export type ReplaceOutgoingVideoTrack = (
  stream: MediaStream,
  videoTrack: MediaStreamTrack,
  signal: AbortSignal,
) => Promise<void>;

type BlurState = {
  enabled: boolean;
  error: string | null;
  managedBySystem: boolean;
  mode: BackgroundBlurMode;
  status: BackgroundBlurStatus;
};

const PREFERENCE_KEY = 'censis:calling:background-blur-enabled';
const MEDIA_TIMEOUT_MS = 5000;
const idleState = (enabled: boolean): BlurState => ({
  enabled,
  error: null,
  managedBySystem: false,
  mode: null,
  status: enabled ? 'starting' : 'off',
});

function readPreference(): boolean {
  try {
    return localStorage.getItem(PREFERENCE_KEY) === 'true';
  } catch {
    return false;
  }
}
function writePreference(enabled: boolean): void {
  try {
    localStorage.setItem(PREFERENCE_KEY, String(enabled));
  } catch {
    /* Storage can be unavailable in locked-down profiles. */
  }
}
function nativeCapability(track: MediaStreamTrack) {
  const capabilities = track.getCapabilities?.().backgroundBlur ?? [];
  return {
    controllable: capabilities.includes(true) && capabilities.includes(false),
    enabled: track.getSettings?.().backgroundBlur === true,
  };
}

/** Own blur preferences and cancellable processing, while the media hook keeps
 * ownership of camera streams, preview attachment, and WebRTC senders. */
export function useBackgroundBlur(
  rawStreamRef: MutableRefObject<MediaStream | null>,
  replaceOutgoingVideoTrack: ReplaceOutgoingVideoTrack,
) {
  const [state, setState] = useState(() => idleState(readPreference()));
  const stateRef = useRef(state);
  const desiredRef = useRef(state.enabled);
  const operationRef = useRef<AbortController | null>(null);
  const processorRef = useRef<SoftwareBackgroundBlur | null>(null);
  const detachListenerRef = useRef<(() => void) | null>(null);
  const runtimeFailureRef = useRef<(error: Error) => void>(() => {});

  const publish = useCallback((next: BlurState) => {
    const previous = stateRef.current;
    if (
      next.status === 'active' &&
      (previous.status !== 'active' ||
        previous.mode !== next.mode ||
        previous.managedBySystem !== next.managedBySystem)
    ) {
      console.info(`[Background blur] Active mode: ${next.mode}`, {
        mode: next.mode,
        managedBySystem: next.managedBySystem,
      });
    }
    stateRef.current = next;
    setState(next);
  }, []);

  const assertCurrent = useCallback(
    (stream: MediaStream, signal: AbortSignal) => {
      throwIfBlurAborted(signal);
      if (rawStreamRef.current !== stream) throw backgroundBlurAbortError();
    },
    [rawStreamRef],
  );

  const applyNativeConstraint = useCallback(
    async (track: MediaStreamTrack, enabled: boolean, signal: AbortSignal) => {
      throwIfBlurAborted(signal);
      await waitForOperation(
        track.applyConstraints({
          ...track.getConstraints(),
          backgroundBlur: enabled,
        }),
        signal,
        MEDIA_TIMEOUT_MS,
        'Timed out changing camera background blur',
      );
      throwIfBlurAborted(signal);
    },
    [],
  );

  const stopSoftware = useCallback(async (signal: AbortSignal) => {
    throwIfBlurAborted(signal);
    if (processorRef.current) {
      await waitForOperation(
        processorRef.current.stop(),
        signal,
        MEDIA_TIMEOUT_MS,
        'Timed out stopping background blur',
      );
    }
    throwIfBlurAborted(signal);
  }, []);

  const reportFailure = useCallback(
    async (error: Error, stream: MediaStream, signal: AbortSignal) => {
      if (signal.aborted || rawStreamRef.current !== stream) return;
      const track = stream.getVideoTracks()[0];
      try {
        if (track) {
          const native = nativeCapability(track);
          if (native.controllable && native.enabled) {
            await applyNativeConstraint(track, false, signal);
          }
          assertCurrent(stream, signal);
          await replaceOutgoingVideoTrack(stream, track, signal);
        }
        assertCurrent(stream, signal);
        await stopSoftware(signal);
      } catch (restoreError) {
        if (signal.aborted || rawStreamRef.current !== stream) return;
        wlog('error', 'Failed to restore raw video after blur failure', {
          component: 'background-blur',
          blur_restore_error: String(restoreError),
        });
      }
      if (signal.aborted || rawStreamRef.current !== stream) return;
      desiredRef.current = false;
      writePreference(false);
      publish({
        ...idleState(false),
        error: error.message,
        status:
          error instanceof SoftwareBackgroundBlurError && error.unavailable
            ? 'unavailable'
            : 'off',
      });
      wlog('error', 'Background blur failed; restored raw camera video', {
        component: 'background-blur',
        blur_error_name: error.name,
        blur_error_message: error.message,
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'background-blur');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        Sentry.captureException(error);
      });
    },
    [
      applyNativeConstraint,
      assertCurrent,
      publish,
      rawStreamRef,
      replaceOutgoingVideoTrack,
      stopSoftware,
    ],
  );

  const applyBlur = useCallback(
    async (
      stream: MediaStream,
      enabled: boolean,
      signal: AbortSignal,
    ): Promise<BlurState> => {
      assertCurrent(stream, signal);
      const track = stream.getVideoTracks()[0];
      if (!track)
        throw new SoftwareBackgroundBlurError(
          'No camera video track is available for background blur',
        );
      const native = nativeCapability(track);
      if (native.enabled && !native.controllable) {
        await replaceOutgoingVideoTrack(stream, track, signal);
        assertCurrent(stream, signal);
        await stopSoftware(signal);
        assertCurrent(stream, signal);
        return {
          enabled: true,
          mode: 'native',
          managedBySystem: true,
          status: 'active',
          error: null,
        };
      }
      if (!enabled) {
        if (native.controllable && native.enabled) {
          await applyNativeConstraint(track, false, signal);
          if (nativeCapability(track).enabled)
            throw new SoftwareBackgroundBlurError(
              'The camera did not disable background blur',
            );
        }
        assertCurrent(stream, signal);
        await replaceOutgoingVideoTrack(stream, track, signal);
        assertCurrent(stream, signal);
        await stopSoftware(signal);
        assertCurrent(stream, signal);
        return idleState(false);
      }
      if (native.controllable) {
        try {
          await applyNativeConstraint(track, true, signal);
          assertCurrent(stream, signal);
          if (!nativeCapability(track).enabled)
            throw new SoftwareBackgroundBlurError(
              'The camera did not enable native background blur',
            );
          await replaceOutgoingVideoTrack(stream, track, signal);
          assertCurrent(stream, signal);
          await stopSoftware(signal);
          assertCurrent(stream, signal);
          return {
            enabled: true,
            mode: 'native',
            managedBySystem: false,
            status: 'active',
            error: null,
          };
        } catch (error) {
          assertCurrent(stream, signal);
          wlog('warn', 'Native background blur failed; using software', {
            component: 'background-blur',
            native_blur_error: String(error),
          });
          try {
            await applyNativeConstraint(track, false, signal);
          } catch {
            assertCurrent(stream, signal);
          }
        }
      }
      assertCurrent(stream, signal);
      if (!SoftwareBackgroundBlur.isSupported()) {
        throw new SoftwareBackgroundBlurError(
          'Background blur is not supported by this browser',
          true,
        );
      }
      if (!processorRef.current) {
        processorRef.current = new SoftwareBackgroundBlur((error) =>
          runtimeFailureRef.current(error),
        );
      }
      // This callback belongs to this camera operation, including after startup.
      runtimeFailureRef.current = (error) => {
        void reportFailure(error, stream, signal);
      };
      const output = await processorRef.current.start(stream, signal);
      assertCurrent(stream, signal);
      await replaceOutgoingVideoTrack(output.stream, output.videoTrack, signal);
      assertCurrent(stream, signal);
      return {
        enabled: true,
        mode: 'software',
        managedBySystem: false,
        status: 'active',
        error: null,
      };
    },
    [
      applyNativeConstraint,
      assertCurrent,
      replaceOutgoingVideoTrack,
      reportFailure,
      stopSoftware,
    ],
  );

  const run = useCallback(
    async (
      stream: MediaStream,
      enabled: boolean,
      parentSignal?: AbortSignal,
    ) => {
      throwIfBlurAborted(parentSignal);
      operationRef.current?.abort(backgroundBlurAbortError());
      const operation = new AbortController();
      operationRef.current = operation;
      const { signal } = operation;
      const abort = () => operation.abort(parentSignal?.reason);
      parentSignal?.addEventListener('abort', abort, { once: true });
      const previous = stateRef.current;
      publish({ ...previous, enabled, status: 'starting', error: null });
      try {
        const next = await applyBlur(stream, enabled, signal);
        assertCurrent(stream, signal);
        if (!next.managedBySystem) {
          desiredRef.current = next.enabled;
          writePreference(next.enabled);
        }
        publish(next);
      } catch (error) {
        if (signal.aborted || rawStreamRef.current !== stream) return;
        const normalized =
          error instanceof Error ? error : new Error(String(error));
        if (!enabled && previous.status === 'active') {
          desiredRef.current = previous.enabled;
          publish({ ...previous, error: normalized.message });
        } else {
          await reportFailure(normalized, stream, signal);
        }
        throw normalized;
      } finally {
        parentSignal?.removeEventListener('abort', abort);
      }
    },
    [applyBlur, assertCurrent, publish, rawStreamRef, reportFailure],
  );

  const prepareStream = useCallback(
    async (stream: MediaStream, signal: AbortSignal) => {
      throwIfBlurAborted(signal);
      detachListenerRef.current?.();
      const track = stream.getVideoTracks()[0];
      if (!track) return;
      const onConfigurationChange = () => {
        if (
          rawStreamRef.current !== stream ||
          stateRef.current.status === 'starting'
        )
          return;
        const native = nativeCapability(track);
        const current = stateRef.current;
        if (native.controllable) {
          if (native.enabled) {
            if (desiredRef.current && current.mode === 'native') return;
          } else if (current.mode !== 'native') {
            // Resolution/device configuration changes do not turn off software blur.
            return;
          }
          // Changes outside our own constraint operation came from camera/OS UI.
          desiredRef.current = native.enabled;
        } else if (!native.enabled && !current.managedBySystem) {
          return;
        }
        void run(stream, desiredRef.current).catch(() => {});
      };
      track.addEventListener?.('configurationchange', onConfigurationChange);
      detachListenerRef.current = () =>
        track.removeEventListener?.(
          'configurationchange',
          onConfigurationChange,
        );
      try {
        await run(stream, desiredRef.current, signal);
      } catch {
        /* Failure recovery keeps the raw camera available for calling. */
      }
    },
    [rawStreamRef, run],
  );

  const setBackgroundBlurEnabled = useCallback(
    async (enabled: boolean) => {
      if (stateRef.current.managedBySystem) return;
      const stream = rawStreamRef.current;
      if (!stream)
        throw new SoftwareBackgroundBlurError(
          'The camera must be ready before background blur can be changed',
        );
      desiredRef.current = enabled;
      await run(stream, enabled);
    },
    [rawStreamRef, run],
  );

  const reset = useCallback(
    (dispose = false) => {
      operationRef.current?.abort(backgroundBlurAbortError());
      operationRef.current = null;
      detachListenerRef.current?.();
      detachListenerRef.current = null;
      if (dispose) {
        void processorRef.current?.dispose();
        processorRef.current = null;
      } else {
        void processorRef.current?.stop();
      }
      publish(idleState(desiredRef.current));
    },
    [publish],
  );

  return {
    backgroundBlurEnabled: state.enabled,
    backgroundBlurError: state.error,
    backgroundBlurManagedBySystem: state.managedBySystem,
    backgroundBlurMode: state.mode,
    backgroundBlurStatus: state.status,
    setBackgroundBlurEnabled,
    prepareStream,
    reset,
  };
}
